import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Star,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { fastapiService } from '../../services/fastapiService';
import { useAdminBasePath } from '../../hooks/useAdminBasePath';
import { InvoiceTemplateEditor } from './InvoiceTemplateEditor';
import {
  defaultTemplateConfig,
  normalizeTemplateConfig,
  templateConfigToRecord,
  type InvoiceTemplateConfig,
} from './invoiceTemplateConfig';

type InvoiceTemplateRow = {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  workspace_id?: string | null;
  is_default: boolean;
  is_active: boolean;
  template_config?: Record<string, unknown>;
  updated_at?: string;
};

export const InvoiceTemplatesPage: React.FC = () => {
  const base = useAdminBasePath();
  const isEnterprise = base.includes('/app');

  const [rows, setRows] = useState<InvoiceTemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [workspaceFilter, setWorkspaceFilter] = useState('');

  const [modal, setModal] = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<InvoiceTemplateRow | null>(null);

  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [formWorkspaceId, setFormWorkspaceId] = useState('');
  const [formIsDefault, setFormIsDefault] = useState(false);
  const [formIsActive, setFormIsActive] = useState(true);
  const [config, setConfig] = useState<InvoiceTemplateConfig>(defaultTemplateConfig());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const ws = workspaceFilter.trim();
      const res = (await fastapiService.getInvoiceTemplates({
        include_inactive: includeInactive,
        workspace_id: /^[0-9a-f-]{36}$/i.test(ws) ? ws : undefined,
      })) as unknown as { data?: InvoiceTemplateRow[]; total?: number };
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load templates');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [includeInactive, workspaceFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!message) return;
    const t = window.setTimeout(() => setMessage(null), 4000);
    return () => window.clearTimeout(t);
  }, [message]);

  const openCreate = () => {
    setEditing(null);
    setFormName('');
    setFormDescription('');
    setFormSlug('');
    setFormWorkspaceId('');
    setFormIsDefault(false);
    setFormIsActive(true);
    setConfig(defaultTemplateConfig());
    setModal('create');
  };

  const openEdit = (row: InvoiceTemplateRow) => {
    setEditing(row);
    setFormName(row.name);
    setFormDescription(row.description || '');
    setFormSlug(row.slug);
    setFormWorkspaceId(row.workspace_id || '');
    setFormIsDefault(row.is_default);
    setFormIsActive(row.is_active);
    setConfig(normalizeTemplateConfig(row.template_config));
    setModal('edit');
  };

  const closeModal = () => {
    setModal(null);
    setEditing(null);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      setError('Name is required');
      return;
    }
    const ws = formWorkspaceId.trim();
    if (ws && !/^[0-9a-f-]{36}$/i.test(ws)) {
      setError('Workspace ID must be a valid UUID or empty for platform-wide');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const payload = templateConfigToRecord(config);
      if (modal === 'create') {
        const slug = formSlug.trim() || undefined;
        await fastapiService.createInvoiceTemplate({
          name: formName.trim(),
          description: formDescription.trim() || null,
          workspace_id: ws || null,
          slug,
          is_default: formIsDefault,
          template_config: payload,
        });
        setMessage('Template created');
      } else if (modal === 'edit' && editing) {
        await fastapiService.updateInvoiceTemplate(editing.id, {
          name: formName.trim(),
          description: formDescription.trim() || null,
          template_config: payload,
          is_active: formIsActive,
          is_default: formIsDefault,
        });
        setMessage('Template updated');
      }
      closeModal();
      await load();
    } catch (e: unknown) {
      const err = e as { error?: string; message?: string };
      setError(typeof err?.error === 'string' ? err.error : e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: InvoiceTemplateRow) => {
    if (!window.confirm(`Delete template “${row.name}”? This cannot be undone.`)) return;
    try {
      setError(null);
      await fastapiService.deleteInvoiceTemplate(row.id);
      setMessage('Template deleted');
      await load();
    } catch (e: unknown) {
      const err = e as { error?: string };
      setError(typeof err?.error === 'string' ? err.error : 'Delete failed');
    }
  };

  const handleSetDefault = async (row: InvoiceTemplateRow) => {
    try {
      setError(null);
      await fastapiService.setDefaultInvoiceTemplate(row.id);
      setMessage('Default template updated');
      await load();
    } catch (e: unknown) {
      const err = e as { error?: string };
      setError(typeof err?.error === 'string' ? err.error : 'Could not set default');
    }
  };

  const tableWrap = isEnterprise
    ? 'overflow-hidden rounded-xl border border-enterprise-200 bg-white shadow-sm'
    : 'overflow-hidden rounded-lg border border-admin-200 bg-white shadow-sm';

  const thClass = isEnterprise
    ? 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-enterprise-500'
    : 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-admin-500';

  return (
    <div>
      <div className="mb-6">
        <Link
          to={`${base}/settings`}
          className={
            isEnterprise
              ? 'inline-flex items-center gap-1 text-sm font-medium text-enterprise-600 hover:text-emerald-800'
              : 'inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-800'
          }
        >
          <ArrowLeft className="h-4 w-4" />
          Settings
        </Link>
        <h1
          className={
            isEnterprise ? 'mt-3 text-2xl font-bold text-enterprise-900' : 'mt-3 text-2xl font-bold text-admin-900'
          }
        >
          Invoice templates
        </h1>
        <p className={isEnterprise ? 'mt-1 text-enterprise-600' : 'mt-1 text-admin-600'}>
          Design invoice PDFs with the full editor (General, Header &amp; Footer, live preview). Data is stored in{' '}
          <code className="rounded bg-slate-100 px-1 text-sm">template_config</code> for the future PDF pipeline.
        </p>
      </div>

      {message && (
        <div
          className={
            isEnterprise
              ? 'mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900'
              : 'mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900'
          }
        >
          {message}
        </div>
      )}
      {error && (
        <div
          className={
            isEnterprise
              ? 'mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900'
              : 'mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800'
          }
        >
          {error}
        </div>
      )}

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col text-xs font-medium text-admin-700">
            <span className={isEnterprise ? 'text-enterprise-600' : 'text-admin-600'}>Workspace ID (optional)</span>
            <input
              type="text"
              placeholder="Platform only — leave empty"
              value={workspaceFilter}
              onChange={(e) => setWorkspaceFilter(e.target.value)}
              className={
                isEnterprise
                  ? 'mt-1 w-64 rounded-md border border-enterprise-300 px-3 py-2 text-sm text-enterprise-900'
                  : 'mt-1 w-64 rounded-md border border-admin-300 px-3 py-2 text-sm'
              }
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="rounded border-admin-300"
            />
            <span className={isEnterprise ? 'text-enterprise-700' : 'text-admin-700'}>Show inactive</span>
          </label>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className={
            isEnterprise
              ? 'inline-flex items-center justify-center gap-2 rounded-lg bg-enterprise-accent px-4 py-2 text-sm font-semibold text-white hover:opacity-95'
              : 'inline-flex items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700'
          }
        >
          <Plus className="h-4 w-4" />
          New template
        </button>
      </div>

      <div className={tableWrap}>
        {loading ? (
          <p className="p-8 text-center text-admin-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center text-admin-500">No templates yet. Create one to get started.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-admin-200">
              <thead className={isEnterprise ? 'bg-enterprise-50' : 'bg-admin-50'}>
                <tr>
                  <th className={thClass}>Name</th>
                  <th className={thClass}>Slug</th>
                  <th className={thClass}>Scope</th>
                  <th className={thClass}>Status</th>
                  <th className={`${thClass} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-admin-100">
                {rows.map((row) => (
                  <tr key={row.id} className={isEnterprise ? 'hover:bg-enterprise-50/50' : 'hover:bg-admin-50/80'}>
                    <td className="px-4 py-3 text-sm font-medium text-admin-900">
                      <span className="inline-flex items-center gap-2">
                        {row.name}
                        {row.is_default && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                            <Star className="h-3 w-3" />
                            Default
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-admin-600">{row.slug}</td>
                    <td className="px-4 py-3 text-sm text-admin-600">
                      {row.workspace_id ? row.workspace_id : 'Platform'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {row.is_active ? (
                        <span className="inline-flex items-center gap-1 text-green-700">
                          <CheckCircle2 className="h-4 w-4" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-admin-500">
                          <XCircle className="h-4 w-4" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        {!row.is_default && (
                          <button
                            type="button"
                            title="Set default"
                            onClick={() => handleSetDefault(row)}
                            className="rounded p-2 text-amber-700 hover:bg-amber-50"
                          >
                            <Star className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          title="Edit"
                          onClick={() => openEdit(row)}
                          className="rounded p-2 text-admin-600 hover:bg-admin-100"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Delete"
                          onClick={() => handleDelete(row)}
                          className="rounded p-2 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <InvoiceTemplateEditor
          isEnterprise={isEnterprise}
          mode={modal}
          formName={formName}
          setFormName={setFormName}
          formDescription={formDescription}
          setFormDescription={setFormDescription}
          formSlug={formSlug}
          setFormSlug={setFormSlug}
          formWorkspaceId={formWorkspaceId}
          setFormWorkspaceId={setFormWorkspaceId}
          formIsDefault={formIsDefault}
          setFormIsDefault={setFormIsDefault}
          formIsActive={formIsActive}
          setFormIsActive={setFormIsActive}
          config={config}
          setConfig={setConfig}
          saving={saving}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}
    </div>
  );
};
